import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import './Modal.css';

const ICONS = {
  confirm: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <path d="M12 8v4M12 16h.01"/>
    </svg>
  ),
  warning: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
      <path d="M12 9v4M12 17h.01"/>
    </svg>
  ),
  error: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <path d="M15 9l-6 6M9 9l6 6"/>
    </svg>
  ),
  success: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <path d="M9 12l2 2 4-4"/>
    </svg>
  ),
  info: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <path d="M12 16v-4M12 8h.01"/>
    </svg>
  ),
};

/**
 * Reusable modal component.
 *
 * Props:
 *   type        — 'confirm' | 'warning' | 'error' | 'success' | 'info'  (default: 'confirm')
 *   title       — Modal title string
 *   message     — Message body (string or JSX)
 *   onConfirm   — Called when user clicks the confirm button (omit for info/success modals)
 *   onClose     — Called when user cancels or closes (required)
 *   confirmText — Label for confirm button  (default: 'Confirmar')
 *   cancelText  — Label for cancel button   (default: 'Cancelar', pass null to hide)
 */
function Modal({
  type = 'confirm',
  title,
  message,
  onConfirm,
  onClose,
  confirmText = 'Confirmar',
  cancelText  = 'Cancelar',
}) {
  // Close on Escape
  useEffect(() => {
    const handle = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  }, [onClose]);

  const content = (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`modal-box modal-box--${type}`} role="dialog" aria-modal="true">
        <div className={`modal-icon modal-icon--${type}`}>
          {ICONS[type] || ICONS.info}
        </div>

        {title && <div className="modal-title">{title}</div>}
        {message && <div className="modal-message">{message}</div>}

        <div className="modal-actions">
          {cancelText && (
            <button className="btn btn-secondary" onClick={onClose}>
              {cancelText}
            </button>
          )}
          {onConfirm && (
            <button
              className={`btn ${type === 'error' || type === 'warning' ? 'btn-danger' : 'btn-primary'}`}
              onClick={onConfirm}
            >
              {confirmText}
            </button>
          )}
          {!onConfirm && (
            <button className="btn btn-primary" onClick={onClose}>
              {confirmText}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
}

export default Modal;
